import { LeftOutlined, RightOutlined, ShopOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  ConfigProvider,
  Skeleton,
  Space,
  Table,
  Typography,
} from "antd";
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import useAuthApi from "../../utils/useAuthApi";

const UserManagersModal = lazy(() => import("./UserManagersModal"));
const EditStoreModal = lazy(
  () => import("./stores-management/EditBranchModal")
);
const StoresManagerModal = lazy(
  () => import("./stores-management/StoresManagerModal")
);

const WRAPPER = { width: "100%", display: "flex", justifyContent: "center" };
const INNER = { width: "90%" };
const CENTER = {
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
};

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "");

export default function ControlPanel() {
  const {
    getUsersData,
    postUserData,
    patchUserData,
    deleteUserData,
    getUsersWithBranches,
  } = useAuthApi();

  const { user } = useAuth();
  const { getClients, getBranches } = useApi();

  const [showUserModal, setShowUserModal] = useState(false);
  const [showStoresModal, setShowStoresModal] = useState(false);
  const [showEditStoreModal, setShowEditStoreModal] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [users, setUsers] = useState([]);
  const [loggedUser, setLoggedUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const cardsPerPage = 5;
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      if (!user?.user_id) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const [single, usersBranches, fetchedClients, fetchedBranches] =
          await Promise.all([
            getUsersData(user.user_id),
            getUsersWithBranches(),
            getClients(),
            getBranches(),
          ]);

        setLoggedUser(Array.isArray(single) ? single[0] : single);
        setUsers(Array.isArray(usersBranches) ? usersBranches : []);
        setClients(fetchedClients?.data || fetchedClients);
        setBranches(fetchedBranches?.data || fetchedBranches);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.user_id]);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => (m[c.id] = c.name));
    return m;
  }, [clients]);

  const branchCards = useMemo(
    () =>
      branches.map((b) => ({
        id: b.id,
        branchName: b.name,
        clientName: clientMap[b.client] || "—",
        clientId: b.client,
        ip: b.ip_address,
        store_code: b.store_code,
      })),
    [branches, clientMap]
  );

  const totalPages = Math.max(1, Math.ceil(branchCards.length / cardsPerPage));

  useEffect(() => {
    if (page >= totalPages) setPage(totalPages - 1);
  }, [totalPages, page]);

  const currentCards = useMemo(
    () =>
      branchCards.slice(
        page * cardsPerPage,
        page * cardsPerPage + cardsPerPage
      ),
    [branchCards, page]
  );

  const columns = useMemo(
    () => [
      { title: "Usuário", dataIndex: "usuario", key: "usuario" },
      { title: "Email", dataIndex: "email", key: "email" },
      { title: "Cargo", dataIndex: "cargo", key: "cargo" },
      { title: "Cliente", dataIndex: "cliente", key: "cliente" },
      { title: "Filial", dataIndex: "filiais", key: "filiais" },
    ],
    []
  );

  const dataSource = useMemo(
    () =>
      users.map((u) => {
        const branchList = u.branches || [];
        const branchNames = branchList.map((b) => b.branch_name);
        const clientNames = [...new Set(branchList.map((b) => b.client_name))];

        return {
          key: u.id,
          usuario: `${u.first_name} ${u.last_name}`,
          email: u.email,
          cargo: u.role,
          cliente: clientNames.length ? clientNames.join(", ") : "—",
          filiais: branchNames.length ? branchNames.join(", ") : "—",
        };
      }),
    [users]
  );

  const handleUserRowClick = useCallback((record) => {
    setSelectedUserId(record.key);
    setShowUserModal(true);
  }, []);

  if (isLoading) {
    return (
      <ConfigProvider>
        <div style={WRAPPER}>
          <div style={{ ...INNER, marginTop: 20 }}>
            <Skeleton active />
          </div>
        </div>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={{ components: { Card: { headerBg: "#d0d6e2" } } }}>
      <div style={WRAPPER}>
        <div style={INNER}>
          <Card title="Painel de Controle">
            {/* ----------- header ----------- */}
            <Typography.Title level={4} style={{ marginTop: 8 }}>
              {loggedUser
                ? `Seja bem-vindo, ${cap(loggedUser.first_name)} ${cap(
                    loggedUser.last_name
                  )}`
                : "Seja bem-vindo"}
            </Typography.Title>
            <Typography.Text strong>
              Cargo: {loggedUser?.role || "N/A"}
            </Typography.Text>

            <Card
              title="Quadro de Clientes e Filiais"
              style={{ marginTop: 24, marginBottom: 20 }}
              bordered={false}>
              <Button
                type="primary"
                style={{ marginBottom: 16 }}
                onClick={() => setShowStoresModal(true)}>
                Gerenciar Clientes e Filiais
              </Button>

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ margin: "8px 0 4px" }}>
                  Você está cadastrado nas seguintes lojas:
                </Typography.Title>
                <Typography.Text type="secondary">
                  Clique no item que deseja editar/excluir
                </Typography.Text>
              </div>

              <Space style={CENTER}>
                <Button
                  shape="circle"
                  icon={<LeftOutlined />}
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(p - 1, 0))}
                />
                {currentCards.map((card) => (
                  <Card
                    key={card.id}
                    hoverable
                    style={{
                      width: 170,
                      height: 140,
                      textAlign: "center",
                      marginRight: 16,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onClick={() => {
                      setSelectedBranch(card);
                      setShowEditStoreModal(true);
                    }}>
                    <ShopOutlined style={{ fontSize: 28, marginBottom: 8 }} />
                    <div>
                      <Typography.Text strong>
                        {card.clientName}
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text>{card.branchName}</Typography.Text>
                    </div>
                  </Card>
                ))}
                <Button
                  shape="circle"
                  icon={<RightOutlined />}
                  disabled={page === totalPages - 1}
                  onClick={() =>
                    setPage((p) => Math.min(p + 1, totalPages - 1))
                  }
                />
              </Space>
            </Card>

            <Card
              title="Quadro de Usuários"
              style={{ marginBottom: 20, marginTop: 35 }}>
              <Button
                type="primary"
                style={{ marginBottom: 24 }}
                onClick={() => {
                  setSelectedUserId(null);
                  setShowUserModal(true);
                }}>
                Gerenciar Usuários
              </Button>

              <Table
                columns={columns}
                dataSource={dataSource}
                pagination={{
                  pageSize: 7,
                  showSizeChanger: false,
                }}
                onRow={(record) => ({
                  onClick: () => handleUserRowClick(record),
                  style: { cursor: "pointer" },
                })}
              />
            </Card>
          </Card>
        </div>
      </div>

      <Suspense fallback={null}>
        {showUserModal && (
          <UserManagersModal
            open={showUserModal}
            onClose={() => setShowUserModal(false)}
            users={users}
            setUsers={setUsers}
            postUserData={postUserData}
            patchUserData={patchUserData}
            deleteUserData={deleteUserData}
            editUserId={selectedUserId}
          />
        )}

        {showStoresModal && (
          <StoresManagerModal
            open={showStoresModal}
            onClose={() => setShowStoresModal(false)}
          />
        )}

        {showEditStoreModal && (
          <EditStoreModal
            open={showEditStoreModal}
            onClose={() => setShowEditStoreModal(false)}
            branch={selectedBranch}
            clients={clients}
            refresh={async () => {
              setShowEditStoreModal(false);
              setIsLoading(true);
              const bs = await getBranches();
              setBranches(bs?.data || bs);
              setIsLoading(false);
            }}
          />
        )}
      </Suspense>
    </ConfigProvider>
  );
}
